// frontend/src/utils/pwa-utils.js

// Register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      //console.log('ServiceWorker registration successful with scope:', registration.scope);
      return registration;
    } catch (error) {
      console.error('ServiceWorker registration failed:', error);
      return null;
    }
  }
  return null;
};

// Check if app is installed
export const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone || 
         document.referrer.includes('android-app://');
};

// Event listener for beforeinstallprompt
let deferredPrompt = null;

export const initInstallPrompt = (callback) => {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent Chrome 67 and earlier from automatically showing the prompt
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    
    // Notify the app that the install prompt is available
    if (callback && typeof callback === 'function') {
      callback(true);
    }
  });
  
  // Listen for app installed event
  window.addEventListener('appinstalled', () => {
    // Log app install
    //console.log('PWA was installed');
    // Reset deferred prompt
    deferredPrompt = null;
    // Notify the app that installation is complete
    if (callback && typeof callback === 'function') {
      callback(false);
    }
  });
  
  // Initial check
  if (callback && typeof callback === 'function') {
    callback(!!deferredPrompt && !isAppInstalled());
  }
};

// Show installation prompt
export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    //console.log('Install prompt not available');
    return false;
  }

  // Show the install prompt
  deferredPrompt.prompt();

  // Wait for the user to respond to the prompt
  const { outcome } = await deferredPrompt.userChoice;
  //console.log(`User response to install prompt: ${outcome}`);

  // Clear the deferred prompt variable, as it can only be used once
  deferredPrompt = null;

  return outcome === 'accepted';
};

// Check if PWA is installed
export const isPWAInstalled = () => {
  return isAppInstalled();
};

// Get device name
export const getDeviceName = () => {
  const userAgent = navigator.userAgent;

  // Check for mobile devices
  if (/Android/i.test(userAgent)) {
    const match = userAgent.match(/Android\s([0-9.]+)/);
    return `Android ${match ? match[1] : 'Device'}`;
  }

  if (/iPhone/i.test(userAgent)) {
    return 'iPhone';
  }

  if (/iPad/i.test(userAgent)) {
    return 'iPad';
  }

  if (/Windows/i.test(userAgent)) {
    return 'Windows PC';
  }

  if (/Mac/i.test(userAgent)) {
    return 'Mac';
  }

  if (/Linux/i.test(userAgent)) {
    return 'Linux PC';
  }

  return 'Unknown Device';
};

// Generate device fingerprint
export const generateDeviceFingerprint = async () => {
  const components = [];

  // User agent
  components.push(navigator.userAgent);

  // Screen resolution
  components.push(`${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);

  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Language
  components.push(navigator.language);

  // Platform
  components.push(navigator.platform);

  // Hardware concurrency (CPU cores)
  components.push(navigator.hardwareConcurrency || 'unknown');

  // Device memory (if available)
  if (navigator.deviceMemory) {
    components.push(navigator.deviceMemory);
  }

  // Touch support
  components.push(navigator.maxTouchPoints || 0);

  // Create fingerprint string
  const fingerprintString = components.join('|');

  // Hash the fingerprint using SHA-256
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprintString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Failed to generate fingerprint hash:', error);
    // Fallback: use a simple hash
    let hash = 0;
    for (let i = 0; i < fingerprintString.length; i++) {
      const char = fingerprintString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(16);
  }
};